import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronRight,
  Home, Globe, Package, BookOpen, CalendarDays, Heart, FileText,
  Megaphone, Users, BarChart3, DollarSign, LogOut, LayoutDashboard, ArrowLeft,
  Settings as SettingsGearIcon,
} from 'lucide-react';
import { ENLogo } from '@/components/ENLogo';
import { useAuth } from '@/context/AuthContext';
import { SETTINGS_GROUPS } from '../settings/settingsGroups';

// Iter 191c — flatten SETTINGS_GROUPS into a single scroll-list of links so
// the sidebar's Settings sub-nav mirrors the full set of settings pages,
// eliminating the duplicate middle rail formerly rendered by SettingsHubLayout.
const SETTINGS_SIDEBAR_LINKS = SETTINGS_GROUPS.flatMap((g) => [
  { header: true, label: g.label, id: `hdr-${g.id}` },
  ...g.items.map((item) => ({ label: item.label, path: item.to, status: item.status })),
]);

/**
 * Squarespace-style left sidebar shell.
 * Top-level nav: Home / Website / Products & Services / Content & Memberships / Scheduling / Donations / Invoicing / Campaigns / Contacts / Analytics / Finance
 * Each item can expand to show sub-items.
 */

// Iter 175j — `comingSoon` items are HIDDEN from clients (they don't see the
// "Soon" badges anymore — work-in-progress features are invisible). Master
// admins viewing via impersonation still see them so we can keep tabling /
// developing them. The `isImpersonating` check below reads sessionStorage,
// matching the impersonation banner contract.
const isMasterImpersonating = () => {
  try { return !!sessionStorage.getItem('masterImpersonation'); }
  catch (e) { return false; }
};

const NAV_GROUPS = [
  { id: 'home', label: 'Home', icon: Home, path: '/website-builder', exact: true },
  {
    id: 'website', label: 'Website', icon: Globe, path: '/website-builder/website',
    children: [
      { label: 'Pages', path: '/website-builder/pages' },
      { label: 'Templates', path: '/website-builder/templates' },
      { label: 'Styles', path: '/website-builder/styles' },
      { label: 'Assets', path: '/website-builder/assets' },
      { label: 'SEO / AIO', path: '/website-builder/seo' },
      { label: 'Custom CSS', path: '/website-builder/custom-css' },
    ],
  },
  {
    id: 'products', label: 'Products & Services', icon: Package, path: '/website-builder/products-services',
    children: [
      { label: 'Products', path: '/website-builder/products' },
      { label: 'Orders', path: '/website-builder/orders' },
      { label: 'Discounts', path: '/website-builder/discounts' },
      { label: 'Selling Tools', path: '/website-builder/selling-tools', comingSoon: true },
    ],
  },
  { id: 'content', label: 'Content & Memberships', icon: BookOpen, path: '/website-builder/content-memberships', comingSoon: true },
  { id: 'scheduling', label: 'Scheduling', icon: CalendarDays, path: '/website-builder/bookings' },
  { id: 'donations', label: 'Donations', icon: Heart, path: '/website-builder/settings/donations' },
  {
    id: 'invoicing', label: 'Invoicing', icon: FileText, path: '/website-builder/invoicing',
    children: [
      { label: 'Dashboard',  path: '/website-builder/invoicing' },
      { label: 'Clients',    path: '/website-builder/invoicing/clients' },
      { label: 'Invoices',   path: '/website-builder/invoices' },
      { label: 'Estimates',  path: '/website-builder/invoicing/estimates', comingSoon: true },
      { label: 'Proposals',  path: '/website-builder/invoicing/proposals', comingSoon: true },
      { label: 'Contracts',  path: '/website-builder/invoicing/contracts', comingSoon: true },
      { label: 'Pay Links',  path: '/website-builder/pay-links' },
    ],
  },
  { id: 'campaigns', label: 'Campaigns', icon: Megaphone, path: '/website-builder/campaigns', comingSoon: true },
  {
    id: 'contacts', label: 'Contacts', icon: Users, path: '/website-builder/customers',
    children: [
      { label: 'Customers',  path: '/website-builder/customers' },
      { label: 'Inquiries',  path: '/website-builder/messages?tab=submissions' },
    ],
  },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/website-builder/analytics' },
  { id: 'finance', label: 'Finance', icon: DollarSign, path: '/website-builder/finances' },
  {
    id: 'settings', label: 'Settings', icon: SettingsGearIcon, path: '/website-builder/settings',
    // Iter 191c — full flat list of every settings page (mirrors SETTINGS_GROUPS)
    // so the user gets ONE scrollable Settings menu in the sidebar instead of
    // two separate settings navigators (was: this + the middle rail rendered
    // by SettingsHubLayout).
    children: SETTINGS_SIDEBAR_LINKS,
  },
];

const ComingSoonBadge = ({ size = 'sm' }) => (
  <span
    className={`shrink-0 ${size === 'sm' ? 'px-1.5 py-[1px] text-[8.5px]' : 'px-2 py-[2px] text-[9px]'} rounded-full bg-[#F59E0B]/15 text-[#FBBF24] font-bold uppercase tracking-[0.08em] border border-[#F59E0B]/25`}
    data-testid="coming-soon-badge"
  >
    Soon
  </span>
);

const isPathActive = (path, currentPath, exact = false) => {
  if (exact) return currentPath === path;
  return currentPath === path || currentPath.startsWith(path + '/');
};

const figureExpandedGroup = (currentPath) => {
  for (const group of NAV_GROUPS) {
    if (!group.children) continue;
    if (isPathActive(group.path, currentPath) || group.children.some(c => isPathActive(c.path, currentPath))) {
      return group.id;
    }
  }
  return null;
};

export default function SqsShell({ children, hideSidebar = false, sidebarSlot = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [expanded, setExpanded] = useState(figureExpandedGroup(location.pathname));

  // Iter 175j — Filter out `comingSoon` entries unless the master is
  // impersonating. Memoised so we don't recompute on every render.
  const showComingSoon = isMasterImpersonating();
  const navGroups = React.useMemo(() => {
    if (showComingSoon) return NAV_GROUPS;
    return NAV_GROUPS
      .filter(g => !g.comingSoon)
      .map(g => g.children
        ? { ...g, children: g.children.filter(c => !c.comingSoon) }
        : g);
  }, [showComingSoon]);

  useEffect(() => {
    const newExpanded = figureExpandedGroup(location.pathname);
    if (newExpanded) setExpanded(newExpanded);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (hideSidebar) {
    return <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] text-white">{children}</div>;
  }

  return (
    <div className="en-purple min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] text-white flex font-sans" data-testid="sqs-shell">
      {/* Background grid pattern — matches VendorDashboard */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      {/* ── LEFT SIDEBAR — visually identical to VendorDashboard ── */}
      <aside
        className="shrink-0 w-[260px] h-screen sticky top-0 bg-[#1E1E3A]/95 backdrop-blur-2xl border-r border-white/5 flex flex-col z-[55]"
        data-testid="sqs-sidebar"
      >
        {/* Logo — click goes to marketplace home */}
        <div className="px-6 pt-7 pb-6">
          <ENLogo size="md" linkTo="/" />
        </div>

        {/* Back to Marketplace */}
        <div className="px-4 mb-2">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-all text-sm"
            data-testid="sqs-back-to-marketplace"
          >
            <ArrowLeft size={16} />
            Back to Marketplace
          </Link>
        </div>

        {/* Super Dashboard Toggle — Website active here */}
        <div className="px-4 mb-4" data-testid="super-dashboard-toggle">
          <div className="bg-[#0E0E23]/60 rounded-xl p-1 flex gap-1">
            <button
              onClick={() => navigate('/dashboard/vendor')}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-[#9CA3AF] hover:text-white transition-all"
              data-testid="switch-to-vendor-btn"
            >
              <LayoutDashboard size={14} /> Vendor
            </button>
            <div className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_12px_rgba(139,92,246,0.3)]">
              <Globe size={14} /> Website
            </div>
          </div>
        </div>

        {/* NAV LIST */}
        <nav className="flex-1 px-4 space-y-1 overflow-y-auto sqs-scrollbar">
          {navGroups.map((group) => {
            const isActiveSelf = isPathActive(group.path, location.pathname, group.exact);
            const hasActiveChild = group.children?.some(c => isPathActive(c.path, location.pathname));
            const active = isActiveSelf || hasActiveChild;
            const isOpen = expanded === group.id;

            return (
              <div key={group.id}>
                <button
                  onClick={() => {
                    if (group.children) {
                      setExpanded(isOpen ? null : group.id);
                      if (!isOpen) navigate(group.children[0].path);
                    } else {
                      navigate(group.path);
                    }
                  }}
                  className={`group w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all duration-200 relative ${
                    active
                      ? 'text-white bg-gradient-to-r from-[#8B5CF6]/30 to-[#3B82F6]/20 shadow-[0_0_20px_rgba(139,92,246,0.2)]'
                      : 'text-[#9CA3AF] hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`sqs-nav-${group.id}`}
                >
                  {active && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-gradient-to-b from-[#8B5CF6] to-[#3B82F6] rounded-r-full" />
                  )}
                  <group.icon
                    size={18}
                    strokeWidth={active ? 2 : 1.5}
                    className={active ? 'text-[#A78BFA]' : 'group-hover:text-white'}
                  />
                  <span className="flex-1 text-left">{group.label}</span>
                  {group.comingSoon && <ComingSoonBadge />}
                  {group.children && (
                    isOpen
                      ? <ChevronDown size={14} className="text-[#64748B]" />
                      : <ChevronRight size={14} className="text-[#64748B]" />
                  )}
                </button>

                {/* CHILD ITEMS */}
                {isOpen && group.children && (
                  <div className="ml-8 mt-1 mb-1 space-y-0.5 border-l border-white/5 pl-3 max-h-[420px] overflow-y-auto sqs-scrollbar">
                    {group.children.map((child, ci) => {
                      // Iter 191c — support flat list with { header: true } separators
                      if (child.header) {
                        return (
                          <div
                            key={child.id || `hdr-${ci}`}
                            className="px-3 pt-3 pb-1 text-[9.5px] uppercase tracking-[0.20em] font-bold text-[#A78BFA]/80 first:pt-0.5"
                          >
                            {child.label}
                          </div>
                        );
                      }
                      const childActive = isPathActive(child.path, location.pathname);
                      const isSoon = child.status === 'soon';
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-[12.5px] transition-all ${
                            childActive ? 'text-white font-semibold' : 'text-[#9CA3AF] hover:text-white'
                          } ${isSoon ? 'opacity-55' : ''}`}
                          data-testid={`sqs-subnav-${child.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                        >
                          <span className="flex-1 truncate">{child.label}</span>
                          {child.comingSoon && <ComingSoonBadge />}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* USER FOOTER — matches VendorDashboard layout */}
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-3 py-2">
            {user?.profile_picture || user?.picture ? (
              <img
                src={(user.profile_picture || user.picture).startsWith('/api/')
                  ? `${process.env.REACT_APP_BACKEND_URL}${user.profile_picture || user.picture}`
                  : (user.profile_picture || user.picture)}
                alt={user?.name}
                className="w-9 h-9 rounded-full object-cover border-2 border-[#8B5CF6]/40 shadow-[0_0_10px_rgba(139,92,246,0.2)]"
                data-testid="sqs-sidebar-profile-pic"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center text-sm font-bold shadow-[0_0_10px_rgba(139,92,246,0.3)]">
                {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
            <button
              onClick={() => navigate('/website-builder/settings')}
              className="flex-1 min-w-0 text-left"
              data-testid="sqs-user-btn"
            >
              <p className="text-sm text-white font-medium truncate">{user?.name || user?.email?.split('@')[0]}</p>
              <p className="text-[11px] text-[#9CA3AF]">Website Builder</p>
            </button>
            <button
              onClick={handleLogout}
              className="text-[#9CA3AF] hover:text-[#FF6B6B] transition-colors"
              data-testid="sqs-logout-btn"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
          {/* Iter 191c — TRIAL / PLAN slim chip. Now sits DIRECTLY under the
              "Website Builder" label per user request, so it no longer covers
              the Settings nav entry above. */}
          {sidebarSlot && (
            <div className="mt-2 px-1" data-testid="sqs-sidebar-slot">
              {sidebarSlot}
            </div>
          )}
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 min-w-0 min-h-screen relative overflow-x-hidden" data-testid="sqs-main">
        {children}
      </main>

      <style>{`
        .sqs-scrollbar::-webkit-scrollbar { width: 4px; }
        .sqs-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .sqs-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }
        .sqs-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.16); }
      `}</style>
    </div>
  );
}

/** Subtle "header bar" that goes inside the main column for each page */
export function SqsPageHeader({ title, subtitle, actions, breadcrumbs, testid }) {
  return (
    <div className="sticky top-0 z-30 bg-[#0E0E23]/95 backdrop-blur-xl border-b border-white/[0.06] px-10 py-6" data-testid={testid || 'sqs-page-header'}>
      {breadcrumbs && (
        <div className="text-[11px] text-[#64748B] uppercase tracking-[0.14em] mb-2 font-medium">{breadcrumbs}</div>
      )}
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <h1 className="text-[28px] font-bold text-white font-syne leading-tight" style={{ letterSpacing: '-0.01em' }}>{title}</h1>
          {subtitle && <p className="text-[#9CA3AF] text-sm mt-1.5 max-w-2xl">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2.5">{actions}</div>}
      </div>
    </div>
  );
}

export function SqsButton({ children, variant = 'primary', size = 'md', icon: Icon, ...props }) {
  const sizes = { sm: 'px-3 py-1.5 text-[12px]', md: 'px-5 py-2.5 text-[13px]', lg: 'px-7 py-3 text-[14px]' };
  const variants = {
    primary: 'bg-white text-[#0E0E23] hover:bg-[#F5F5F5] font-semibold shadow-[0_0_24px_rgba(255,255,255,0.06)]',
    dark: 'bg-[#0E0E23] border border-white/10 text-white hover:bg-[#1A1A3A] font-semibold',
    ghost: 'text-[#9CA3AF] hover:text-white font-medium',
    purple: 'bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white hover:shadow-[0_0_24px_rgba(139,92,246,0.4)] font-semibold',
  };
  return (
    <button className={`${sizes[size]} ${variants[variant]} rounded-lg flex items-center gap-2 transition-all duration-200`} {...props}>
      {Icon && <Icon size={size === 'sm' ? 12 : 14} />}
      {children}
    </button>
  );
}
